import { CONFIG } from '@/infra/config';

const subtle = window.crypto.subtle || new Crypto().subtle;

const CIPHER_TF = 'AES-GCM';
const AES_KEY_SIZE = 256;
const GCM_IV_LENGTH = 12;
const GCM_TAG_LENGTH = 16;

export const getSecret = () => {
    let secKey = CONFIG.APP_SECRET;
    const chunk = AES_KEY_SIZE / 8;
    if (secKey.length < chunk) {
        let missingLength = chunk - secKey.length;
        let sb = secKey;
        while (missingLength > 0) {
            sb += secKey;
            missingLength -= secKey.length;
        }
        secKey = sb;
    }
    secKey = secKey.substring(0, chunk);
    return secKey;
};

export const getKey = async () => {
    try {
        const secKey = getSecret();
        const buf = Buffer.from(secKey, 'utf-8');
        const key = await subtle.importKey('raw', buf, CIPHER_TF, false, [
            'encrypt',
            'decrypt',
        ]);

        return key;
    } catch (err: unknown) {
        console.error(err);
        throw err;
    }
};

export const getIV = () => {
    const iv = new Uint8Array(GCM_IV_LENGTH);
    return crypto.getRandomValues(iv);
};

export const encrypt = async (value: string) => {
    if (value === '') return value;

    const iv = getIV();
    const key = await getKey();
    const data = Buffer.from(value, 'utf-8');
    return webEncrypt(data, key, iv);
};

export const webEncrypt = async (
    value: ArrayBuffer,
    key: CryptoKey,
    iv: Uint8Array
) => {
    try {
        const encrypted = await subtle.encrypt(
            { name: CIPHER_TF, iv, tagLength: GCM_TAG_LENGTH * 8 },
            key,
            value
        );

        const data = new Uint8Array(encrypted);
        const combined = new Uint8Array(iv.length + data.length);
        combined.set(iv);
        combined.set(data, iv.length);
        return Buffer.from(combined.buffer).toString('base64');
    } catch (err: unknown) {
        console.error(err);
        throw err;
    }
};

export const decrypt = async (value: string) => {
    if (value === '') return value;

    const key = await getKey();
    const encrypted = Buffer.from(value, 'base64');
    const combined = new Uint8Array(encrypted);
    const iv = combined.subarray(0, GCM_IV_LENGTH);
    const data = combined.subarray(GCM_IV_LENGTH, combined.length);
    return webDecrypt(data, key, iv);
};

export const webDecrypt = async (
    value: Uint8Array,
    key: CryptoKey,
    iv: Uint8Array
) => {
    try {
        const decrypted = await subtle.decrypt(
            { name: CIPHER_TF, iv, tagLength: GCM_TAG_LENGTH * 8 },
            key,
            value
        );
        return Buffer.from(decrypted).toString('utf-8');
    } catch (err: unknown) {
        console.error(err);
        throw err;
    }
};
